import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
    DeactivateContractDealsInput,
    GetContractDealsByIdsInput,
    GetContractDealsByIdsOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import closeCircleTokens from '@amzn/meridian-tokens/base/icon/close-circle';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { ColDef } from 'ag-grid-community';
import { orderBy } from 'lodash';
import { Link, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { ProtectedRoute } from 'src/components/ProtectedRoute';
import { DEFAULT_TOASTER_TIMEOUT, Status } from 'src/constants';
import { EXCEL_DATE_STYLE_ID, LINK_COLOR } from 'src/constants/agGrid';
import { EDITOR_ROLE_SET, VIEWER_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IDealsRowData } from 'src/types/IDeals';
import { IToast } from 'src/types/IToaster';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import {
    getLastUpdatedYYYYMMDDStrFromTimestamp,
    getStatusFromStartEndDates,
    getYYYYMMDDUTCStrFromTimestamp
} from 'src/util/grid/cellGetter';
import { getStatusCellRenderer } from 'src/util/grid/cellRenderer';
import { dateFilterOptions } from 'src/util/grid/filter';
import { dateComparator, dateFilterComparator } from 'src/util/grid/sort';

import { AddDeal } from '../AddDeal';
import { BaseAgGrid } from '../BaseAgGrid';
import { ButtonGroupForGrid } from '../ButtonGroupForGrid';
import { FilterInput } from '../FilterInput';
import './Deals.scss';
import { EndDateDealModal } from './EndDateDealModal';

export const Deals: React.FC = () => {
    const [agGridApi, setAgGridApi] = useState<any>();
    const {
        setTopAlertMsg,
        userRole,
        setToasts,
        userInfo,
        commentSheetAttributes,
        setCommentSheetAttributes,
        setCommentStatus
    } = useContext(AppContext);
    const [dealsRowData, setDealsRowData] = useState<IDealsRowData[]>([]);
    const [dealsDataLoaded, setDealsDataLoaded] = useState<boolean>(false);
    const [filterText, setFilterText] = useState('');
    const { path } = useRouteMatch();
    const { pathname } = useLocation();
    const [selectedDeals, setSelectedDeals] = useState<IDealsRowData[]>([]);
    const [selectedDealToCancel, setSelectedDealToCancel] = useState<IDealsRowData>();
    const [selectedDealToEndDate, setSelectedDealToEndDate] = useState<IDealsRowData>();
    const [isCancelDealModalOpen, setIsCancelDealModalOpen] = useState(false);
    const [cancelDealModalComment, setCancelDealModalComment] = useState<string>('');
    const [requireRerun, setRequireRerun] = useState<boolean>(false);
    const [batchCommentModalOpen, setBatchCommentModalOpen] = useState(false);
    useEffect(() => {
        if (pathname !== ROUTES.DEALS.INDEX) return;

        // Comment out comment feature as it's not in production scope
        // setCommentSheetAttributes((prevState: any) => ({ ...prevState, openCommentSheet: false }));

        getDeals();
    }, [pathname]);

    const getDeals = () => {
        contractConfigurationClient
            .getContractDealsByIds(
                GetContractDealsByIdsInput.clone({
                    includeDeletedContractDeals: true,
                    includeIneligibleContractDeals: true
                })
            )
            .subscribe(
                (response: GetContractDealsByIdsOutput) => {
                    setDealsDataLoaded(true);
                    updateDealsRowData(response);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('GetContractDealsByIds Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    };

    const deselectAllAgGridRows = () => {
        agGridApi?.deselectAll();
    };

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                field: 'entityName',
                headerName: 'Vendor',
                filter: 'agSetColumnFilter',
                cellRenderer: (params: any) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.VENDORS.INDEX}/${params.data.entityId}`}
                        >
                            {params.value}
                        </Link>
                    );
                },
                width: 350,
                minWidth: 200
            },
            {
                field: 'serviceName',
                headerName: 'Service',
                filter: 'agSetColumnFilter',
                minWidth: 120
            },
            {
                field: 'contractName',
                headerName: 'Contract',
                filter: 'agSetColumnFilter',

                cellRenderer: (params: any) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.RATE_CARDS.INDEX}/${params.data.contractId}`}
                        >
                            {params.value}
                        </Link>
                    );
                },
                width: 350,
                minWidth: 200
            },
            {
                field: 'startDate',
                headerName: 'Start date',
                comparator: dateComparator,
                filter: 'agDateColumnFilter',
                filterParams: {
                    filterOptions: dateFilterOptions,
                    defaultOption: 'greaterThanOrEqual',
                    comparator: dateFilterComparator,
                    includeBlanksInLessThan: true,
                    includeBlanksInGreaterThan: true
                },
                cellClass: EXCEL_DATE_STYLE_ID,
                valueGetter: getYYYYMMDDUTCStrFromTimestamp,
                minWidth: 120
            },
            {
                field: 'endDate',
                headerName: 'End date',
                comparator: dateComparator,
                filter: 'agDateColumnFilter',
                filterParams: {
                    filterOptions: dateFilterOptions,
                    defaultOption: 'lessThanOrEqual',
                    comparator: dateFilterComparator,
                    includeBlanksInLessThan: true,
                    includeBlanksInGreaterThan: true
                },
                cellClass: EXCEL_DATE_STYLE_ID,
                valueFormatter: emptyCellFormatter,
                valueGetter: getYYYYMMDDUTCStrFromTimestamp,
                minWidth: 120
            },
            {
                field: 'changedDate',
                headerName: 'Last updated',
                comparator: dateComparator,
                filter: 'agDateColumnFilter',
                filterParams: {
                    filterOptions: dateFilterOptions,
                    defaultOption: 'greaterThanOrEqual',
                    comparator: dateFilterComparator,
                    includeBlanksInLessThan: true,
                    includeBlanksInGreaterThan: true
                },
                cellClass: EXCEL_DATE_STYLE_ID,
                valueGetter: getLastUpdatedYYYYMMDDStrFromTimestamp,
                minWidth: 150
            },
            {
                field: 'status',
                headerName: 'Status',
                valueGetter: getStatusFromStartEndDates,
                cellRenderer: getStatusCellRenderer,
                cellRendererParams: {
                    displayText: true
                },
                minWidth: 120
            },
            {
                field: 'action',
                headerName: 'Actions',
                filter: false,
                cellRenderer: (params: any) => (
                    <Column height="100%" alignmentVertical="center">
                        <Row>
                            {!params.data.isDeleted && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => handleCancelDealBtnClick(params.data)}
                                    disabled={!EDITOR_ROLE_SET.has(userRole)}
                                    data-testid="cancel-deal-btn"
                                >
                                    <Icon className="button-icon" tokens={closeCircleTokens} />
                                </Button>
                            )}
                            {!params.data.isDeleted && !params.data.isDraft && !params.data.endDate && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => setSelectedDealToEndDate(params.data)}
                                    disabled={!EDITOR_ROLE_SET.has(userRole)}
                                    data-testid="enddate-deal-btn"
                                >
                                    <Icon className="button-icon" tokens={editTokens} />
                                </Button>
                            )}
                        </Row>
                    </Column>
                ),
                minWidth: 120,
                maxWidth: 120
            }
            // Comment out comment feature as it's not in production scope
            // {
            //     field: 'action',
            //     headerName: 'Actions',
            //     cellRenderer: getCommentsRenderer,
            //     cellRendererParams: {
            //         deselectAllAgGridRows: deselectAllAgGridRows,
            //         commentSheetAttributes: commentSheetAttributes,
            //         category: CommentCategory.Deals
            //     }
            // }
        ],
        [] //[JSON.stringify(commentSheetAttributes)] // Comment out comment feature as it's not in production scope
    );

    const setDefaultFilter = () => {
        const statusFilterModel = {
            status: {
                filterType: 'set',
                values: [Status.Active]
            }
        };
        agGridApi?.setFilterModel(statusFilterModel);
    };

    useEffect(() => {
        if (agGridApi) setDefaultFilter();
    }, [agGridApi]);

    const handleCancelDealBtnClick = (dealRowData: IDealsRowData) => {
        setRequireRerun(false);

        const [currMon, currDay, currYear] = new Date().toLocaleDateString().split('/');
        const rerunDate = new Date(Number(currYear), Number(currMon) - 2, 1);

        const dealStartDate = new Date(dealRowData.startDate!);
        const dealEndDate = dealRowData.endDate ? new Date(dealRowData.endDate) : null;

        if (dealStartDate < rerunDate && (!dealEndDate || (dealEndDate && dealEndDate > rerunDate))) {
            setRequireRerun(true);
        }

        setSelectedDealToCancel(dealRowData);
        setIsCancelDealModalOpen(true);
    };

    const handleEndDateDealModalOnClose = (refreshData = false) => {
        setSelectedDealToEndDate(undefined);
        if (refreshData) {
            setDealsDataLoaded(false);
            getDeals();
        }
    };

    const handleSubmitWithToast = () => {
        if (selectedDealToCancel === undefined) return;

        contractConfigurationClient
            .deactivateContractDeals(
                DeactivateContractDealsInput.clone({
                    changedBy: userInfo.username,
                    entityContractIds: [selectedDealToCancel.entityContractId!]
                })
            )
            .subscribe(
                () => {
                    setDealsDataLoaded(false);

                    const toastMessage = `Deal canceled`;
                    const toast: IToast = {
                        id: 'cancel-deal-toast',
                        timeout: DEFAULT_TOASTER_TIMEOUT * 2,
                        message: toastMessage
                    };

                    setToasts([toast]);
                    setIsCancelDealModalOpen(false);

                    setSelectedDealToCancel(undefined);
                    setCancelDealModalComment('');

                    getDeals();

                    // Comment out comment feature as it's not in production scope
                    // contractConfigurationClient
                    //     .addComments(
                    //         AddCommentsInput.clone({
                    //             category: CommentCategory.Deals,
                    //             categoryUniqueColumnIds: selectedDeals.map(
                    //                 (deal) => deal.entityContractId
                    //             ) as IntegerList,
                    //             comment: cancelDealModalComment.trim(),
                    //             user: userInfo.username
                    //         })
                    //     )
                    //     .subscribe(
                    //         (response: AddCommentsOutput) => {
                    //             if (response.comments) {
                    //                 setCommentStatus((prevState) => [
                    //                     ...prevState,
                    //                     ...(selectedDeals.map((deal) => deal.entityContractId) as IntegerList)
                    //                 ]);
                    //                 setCancelDealModalComment('');
                    //             }
                    //         },
                    //         (error: UnknownCoralError) => {
                    //             // eslint-disable-next-line no-console
                    //             console.error('AddComments Unexpected error', error.receivedException.message);
                    //             setTopAlertMsg(error.receivedException.message);
                    //         }
                    //     );
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('DeactivateContractDeals Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    };

    const updateDealsRowData = (response: GetContractDealsByIdsOutput) => {
        const deals = response.contractDeals?.reduce((accu, curr) => {
            const { entityActiveIntervals, ...rateCardInfo } = curr;
            const dealsArrPerRateCard = curr.entityActiveIntervals
                ?.filter((entityActiveInterval) => entityActiveInterval.entityId && entityActiveInterval.entityName)
                .map((entityActiveInterval) => {
                    return {
                        ...rateCardInfo,
                        ...entityActiveInterval,
                        changedDate: entityActiveInterval.changedDate || entityActiveInterval.creationDate
                    } as IDealsRowData;
                });
            if (dealsArrPerRateCard) accu.push(...dealsArrPerRateCard);
            return accu;
        }, [] as IDealsRowData[]);

        // Comment out comment feature as it's not in production scope
        // const categoryUniqueColumnIds = (deals as IDealsRowData[]).map((deal) => deal.entityContractId);
        // updateCategoryCommentState(categoryUniqueColumnIds as IntegerList, CommentCategory.Deals)
        //     .then((CommentStatusList) => setCommentStatus(CommentStatusList as IntegerList))
        //     .catch((error) => setTopAlertMsg(error.message));

        const sortedDeals = orderBy(deals, ['changedDate', 'entityName', 'serviceName'], ['desc', 'asc', 'asc']);
        setDealsRowData(sortedDeals ? sortedDeals : []);
    };

    // Comment out comment feature as it's not in production scope
    // const openBatchCommentsModalHandler = () => {
    //     setBatchCommentModalOpen(true);
    //     setCommentSheetAttributes((prevState) => ({
    //         ...prevState,
    //         openCommentSheet: false
    //     }));
    // };

    const dealsContent = (
        <Row height="100%" width="100%" minHeight={480} widths={['fill', 'fit']}>
            <Column className="deals-div" spacing="300">
                <Box type="fill" spacingInset="400">
                    <Row alignmentHorizontal="justify">
                        <FilterInput filterText={filterText} setFilterText={setFilterText} />
                        <Row alignmentVertical="top">
                            <ButtonGroupForGrid showAddDeal={true} />
                        </Row>
                    </Row>
                </Box>

                <div className="deals-grid">
                    <BaseAgGrid
                        columnDefs={columnDefs}
                        rowData={dealsRowData}
                        filterText={filterText}
                        setAgGridApi={setAgGridApi}
                        onFirstDataRendered={setDefaultFilter}
                        loading={!dealsDataLoaded}
                    />
                </div>

                <ConfirmationModal
                    isOpen={isCancelDealModalOpen}
                    onClose={() => setIsCancelDealModalOpen(false)}
                    title="Cancel deals"
                    body={`Are you sure you want to cancel the deal?`}
                    comments={cancelDealModalComment}
                    showComments={true}
                    onCommentsChange={setCancelDealModalComment}
                    warning="Canceling the deal may require reruns for past accounting periods."
                    showWarning={requireRerun}
                    primaryButtonText="Cancel deal"
                    primaryButtonOnClick={handleSubmitWithToast}
                    secondaryButtonText="Don't cancel deal"
                    secondaryButtonOnClick={() => setIsCancelDealModalOpen(false)}
                />
                <EndDateDealModal
                    deal={selectedDealToEndDate}
                    isOpen={!!selectedDealToEndDate}
                    onClose={handleEndDateDealModalOnClose}
                />
                {/* <BatchCommentsModal
                    modalOpen={batchCommentModalOpen}
                    onModalClose={() => setBatchCommentModalOpen(false)}
                    selectedObjectsCount={selectedDeals.length}
                    category={CommentCategory.Deals}
                    categoryUniqueColumnIds={
                        selectedDeals.map((selectedDeal: IDealsRowData) => selectedDeal.entityContractId) as IntegerList
                    }
                    setCommentStatus={setCommentStatus}
                    deselectAllAgGridRows={deselectAllAgGridRows}
                /> */}
            </Column>
            {/* {commentSheetAttributes.openCommentSheet && (
                <div className="comment-sheet">
                    <Comment category={CommentCategory.Deals} />
                </div>
            )} */}
        </Row>
    );

    return (
        <div className="deals-container">
            <Switch>
                <ProtectedRoute exact requiredRoleSet={VIEWER_ROLE_SET} path={path}>
                    {dealsContent}
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={EDITOR_ROLE_SET} path={`${path}${ROUTES.DEALS.ADD}`}>
                    <AddDeal />
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
